<template>
    <modal :show="show" @update:show="handleShowChange" modal-classes="modal-xl shopee-bulk-sync-modal">
        <h5 slot="header" class="modal-title">
            {{ $t("shopee_bulk_sync.title") }}
        </h5>

        <div>
            <!-- #284: the shop is named TWICE on purpose.
                 `shop_name` is what Shopee itself calls it (GET .../shops/:id/info)
                 and `seller_name` is what the Backoffice calls it — a whole-shop
                 sync into the wrong seller is not undone by pressing the button
                 again, so the operator gets to see both names agree before they
                 start. shop_name shows "-" quietly when the fetch fails; it must
                 never gate the modal. -->
            <div class="row mb-3">
                <div class="col-md-5">
                    <label class="form-control-label">{{ $t("shopee_bulk_sync.shop_name") }}</label>
                    <p class="font-weight-bold mb-0">
                        {{ shopeeShopName || "-" }}
                        <i class="fa fa-spinner fa-spin text-muted ml-1" v-if="shopInfoLoading"></i>
                    </p>
                </div>
                <div class="col-md-4">
                    <label class="form-control-label">{{ $t("shopee_bulk_sync.seller_name") }}</label>
                    <p class="font-weight-bold mb-0">{{ sellerNameText || "-" }}</p>
                </div>
                <div class="col-md-3">
                    <label class="form-control-label">{{ $t("shopee_bulk_sync.shop_id") }}</label>
                    <p class="font-weight-bold mb-0">{{ shopIdText || "-" }}</p>
                </div>
            </div>

            <!-- errors. `bulk_in_flight` is NOT one of these: it becomes an
                 attach (see doStart), never a red box. -->
            <div class="row mb-3" v-if="errorKey">
                <div class="col-md-12">
                    <div class="alert alert-danger py-2 px-3 mb-0">
                        <i class="fa fa-exclamation-triangle mr-1"></i>
                        {{ $t(errorKey) }}
                        <span v-if="errorDetail" class="d-block small">{{ errorDetail }}</span>
                    </div>
                </div>
            </div>
            <div class="row mb-3" v-if="attached">
                <div class="col-md-12">
                    <div class="alert alert-info py-2 px-3 mb-0">
                        <i class="fa fa-info-circle mr-1"></i>
                        {{ $t("shopee_bulk_sync.attached_existing") }}
                    </div>
                </div>
            </div>

            <!-- ① settings -->
            <div v-if="showSettings">
                <hr />
                <h6>{{ $t("shopee_bulk_sync.settings_title") }}</h6>

                <div class="row mb-3">
                    <div class="col-md-12">
                        <label class="form-control-label">
                            {{ $t("shopee_bulk_sync.category") }}
                        </label>
                        <el-select
                            v-model="categoryId"
                            :placeholder="$t('shopee_category_map.auto_placeholder')"
                            filterable
                            clearable
                            class="w-100"
                        >
                            <el-option
                                v-for="cat in categoryList"
                                :key="cat.id"
                                :label="cat.name_th || cat.name"
                                :value="cat.id"
                            ></el-option>
                        </el-select>
                        <small class="text-muted d-block">{{ $t("shopee_bulk_sync.category_hint") }}</small>
                        <!-- OPTIONAL since #282: an empty box means "one category
                             per Shopee category, resolved from Shopee itself"
                             (§6D.1), which is the point of the feature. What used
                             to be a red "pick one first" is now a pointer at the
                             page where that automatic choice is corrected. -->
                        <small class="text-muted d-block">
                            {{ $t("shopee_category_map.modal_hint") }}
                            <router-link
                                :to="{ name: 'AppCatalogShopeeCategoryMap' }"
                                target="_blank"
                                class="ml-1"
                            >
                                {{ $t("shopee_category_map.modal_link") }}
                            </router-link>
                        </small>
                    </div>
                </div>

                <div class="row mb-3">
                    <div class="col-md-12">
                        <label class="form-control-label">
                            {{ $t("shopee_bulk_sync.item_status_label") }}
                        </label>
                        <el-checkbox-group v-model="itemStatus">
                            <el-checkbox
                                v-for="st in itemStatusOptions"
                                :key="st"
                                :label="st"
                                class="mr-3"
                            >
                                {{ st }}
                            </el-checkbox>
                        </el-checkbox-group>
                        <small class="text-muted d-block">{{ $t("shopee_bulk_sync.item_status_hint") }}</small>
                        <small class="text-danger d-block" v-if="!itemStatus.length">
                            {{ $t("shopee_bulk_sync.item_status_required_hint") }}
                        </small>
                    </div>
                </div>

                <div class="row mb-3">
                    <div class="col-md-12">
                        <label class="form-control-label d-block">
                            {{ $t("shopee_bulk_sync.only_unlinked") }}
                        </label>
                        <el-switch
                            v-model="onlyUnlinked"
                            active-color="#13ce66"
                            inactive-color="#909399"
                        ></el-switch>
                        <small class="text-muted d-block">{{ $t("shopee_bulk_sync.only_unlinked_hint") }}</small>
                    </div>
                </div>
            </div>

            <!-- ② progress -->
            <div v-if="showProgress">
                <hr />
                <h6>
                    {{ $t("shopee_bulk_sync.progress_title") }}
                    <span class="text-muted">
                        · {{ $t("shopee_bulk_sync.job_label") }} #{{ jobId }}
                    </span>
                    <el-tag size="mini" class="ml-2" :type="isDryRunJob ? 'info' : 'warning'">
                        {{ isDryRunJob ? $t("shopee_bulk_sync.mode_dry_run") : $t("shopee_bulk_sync.mode_real") }}
                    </el-tag>
                    <el-tag size="mini" class="ml-1" :type="jobStatusTagType">
                        {{ $t(jobStatusKey) }}
                    </el-tag>
                </h6>

                <!-- queued / listing have no denominator yet, so there is no bar
                     to draw: a 0% bar reads as "stuck". -->
                <div class="d-flex align-items-center mb-2" v-if="isCounting">
                    <i class="fa fa-spinner fa-spin mr-2"></i>
                    <span>{{ jobStatus === "queued" ? $t("shopee_bulk_sync.queued_hint") : $t("shopee_bulk_sync.listing_hint") }}</span>
                </div>
                <div v-else>
                    <el-progress :percentage="jobPercent" :status="progressBarStatus"></el-progress>
                    <small class="text-muted d-block mt-1">
                        {{ $t("shopee_bulk_sync.processed_of", { processed: jobProcessed, total: jobTotal }) }}
                    </small>
                </div>

                <div class="row mt-3">
                    <div class="col-md-8">
                        <el-tag
                            v-for="c in counters"
                            :key="c.status"
                            size="small"
                            class="mr-2"
                            :type="itemStatusTagType(c.status)"
                        >
                            {{ $t(itemStatusKey(c.status)) }}: {{ c.count }}
                        </el-tag>
                    </div>
                    <div class="col-md-4 text-md-right">
                        <span class="text-muted small mr-2" v-if="!isDryRunJob">
                            {{ $t("shopee_bulk_sync.created_count") }}: {{ jobCreated }}
                            ·
                            {{ $t("shopee_bulk_sync.updated_count") }}: {{ jobUpdated }}
                        </span>
                    </div>
                </div>

                <div class="alert alert-warning py-2 px-3 mt-3 mb-0" v-if="cancelRequested && !isFinished">
                    <i class="fa fa-hourglass-half mr-1"></i>
                    {{ $t("shopee_bulk_sync.cancel_requested") }}
                </div>
                <div class="alert alert-danger py-2 px-3 mt-3 mb-0" v-if="jobLastError">
                    <strong>{{ $t("shopee_bulk_sync.last_error") }}</strong>
                    <span class="d-block small">{{ jobLastError }}</span>
                </div>
                <div
                    class="py-2 px-3 mt-3 mb-0 alert"
                    :class="finishedAlertClass"
                    v-if="isFinished"
                >
                    <i class="fa mr-1" :class="finishedIconClass"></i>
                    {{ $t(finishedTextKey) }}
                </div>
            </div>

            <!-- ③ results -->
            <div v-if="showResults">
                <hr />
                <h6>{{ $t("shopee_bulk_sync.results_title") }}</h6>
                <!-- #286: the Export button sits ON the tab row, not in the
                     footer, because what it exports is what the tabs select —
                     "ทั้งหมด" gives the whole job, "ปฏิเสธ" gives the worklist.
                     Disabled (never hidden) while the job is unfinished: the BE
                     answers 409 bulk_in_flight for that, and a button that
                     disappears leaves the operator wondering where it went. -->
                <div class="d-flex align-items-center justify-content-between">
                    <el-tabs v-model="itemsTab" @tab-click="handleTabChange" class="flex-grow-1 mr-3">
                        <el-tab-pane
                            v-for="tab in itemTabs"
                            :key="tab.name"
                            :name="tab.name"
                            :label="$t(tab.labelKey)"
                        ></el-tab-pane>
                    </el-tabs>
                    <button
                        type="button"
                        class="btn btn-sm btn-outline-success mb-2 text-nowrap"
                        :disabled="exporting || !canExport"
                        @click="handleExport()"
                    >
                        <i class="fa mr-1" :class="exporting ? 'fa-spinner fa-spin' : 'fa-file-excel-o'"></i>
                        {{ $t("shopee_bulk_sync.export_excel") }}
                    </button>
                </div>

                <el-table
                    :data="items"
                    header-row-class-name="thead-light"
                    max-height="380"
                    v-loading="itemsLoading"
                    :empty-text="$t('shopee_bulk_sync.empty_items')"
                >
                    <!-- #284: the id is a LINK to Seller Centre and the Shopee
                         name sits under it. An item id alone is unreadable — the
                         operator cannot tell which of 3,000 listings a reject
                         belongs to without opening it on Shopee. rel="noopener"
                         because target="_blank" otherwise hands the new tab a
                         window.opener back into the Backoffice. -->
                    <el-table-column :label="$t('shopee_bulk_sync.col_item_id')" width="190">
                        <template slot-scope="{ row }">
                            <a
                                v-if="shopeeItemUrl(row.item_id)"
                                class="text-monospace"
                                :href="shopeeItemUrl(row.item_id)"
                                target="_blank"
                                rel="noopener noreferrer"
                                :title="$t('shopee_bulk_sync.open_in_shopee')"
                            >{{ row.item_id }}</a>
                            <span class="text-monospace" v-else>{{ row.item_id }}</span>
                            <span class="text-muted small d-block" v-if="row.item_status">{{ row.item_status }}</span>
                        </template>
                    </el-table-column>
                    <!-- #642/#284: the listing's own name on Shopee. "" for an
                         item that was never fetched (a skip costs no Shopee
                         call), which prints "-" rather than an empty cell.
                         Clamped to two lines with the full text in a tooltip:
                         Shopee names run to 120 characters and would otherwise
                         push every other column off the modal. -->
                    <el-table-column :label="$t('shopee_bulk_sync.col_item_name')" min-width="220">
                        <template slot-scope="{ row }">
                            <el-tooltip
                                v-if="itemNameOf(row)"
                                :content="itemNameOf(row)"
                                placement="top"
                                :open-delay="300"
                            >
                                <span class="shopee-item-name">{{ itemNameOf(row) }}</span>
                            </el-tooltip>
                            <span class="text-muted" v-else>-</span>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('shopee_bulk_sync.col_status')" width="120" align="center">
                        <template slot-scope="{ row }">
                            <el-tag size="mini" :type="itemStatusTagType(row.status)">
                                {{ $t(itemStatusKey(row.status)) }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('shopee_bulk_sync.col_action')" width="110" align="center">
                        <template slot-scope="{ row }">
                            {{ $t(itemActionKey(row)) }}
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('shopee_bulk_sync.col_product')" width="170">
                        <template slot-scope="{ row }">
                            <!-- Only a real product id becomes a link:
                                 /app-catalog/products/0/edit is a dead route. -->
                            <a
                                href="#"
                                v-if="productIdOf(row) > 0"
                                @click.prevent="openInCatalog(row)"
                            >
                                #{{ productIdOf(row) }}
                            </a>
                            <span v-else class="text-muted">-</span>
                        </template>
                    </el-table-column>
                    <!-- #282: the seller category each item landed in, and how
                         it was decided. Without this column a whole-shop run
                         that auto-created 17 categories looks exactly like one
                         that reused 17 — and only the first needs reviewing. -->
                    <el-table-column :label="$t('shopee_category_map.col_category')" min-width="180">
                        <template slot-scope="{ row }">
                            <span v-if="categoryOf(row)">
                                {{ categoryOf(row).name }}
                                <el-tag size="mini" class="ml-1" :type="categoryTagOf(row).type">
                                    {{ $t(categoryTagOf(row).key) }}
                                </el-tag>
                            </span>
                            <span v-else class="text-muted">-</span>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('shopee_bulk_sync.col_reason')" min-width="260">
                        <template slot-scope="{ row }">
                            <span v-if="row.status === 'ok'" class="text-muted small">
                                {{ skuSummary(row) }}
                            </span>
                            <span v-else-if="row.status === 'pending'" class="text-muted">-</span>
                            <span v-else>
                                {{ $t(reasonKey(row)) }}
                                <!-- A rejected item carries the same rejects[] a
                                     single "ส่งเข้า seller" 422 does, so the same
                                     renderer prints them. -->
                                <span
                                    v-for="(rj, idx) in rejectsOf(row)"
                                    :key="idx"
                                    class="d-block text-muted small"
                                >
                                    {{ rejectText(rj) }}
                                    <span v-if="rejectDetail(rj)">— {{ rejectDetail(rj) }}</span>
                                </span>
                                <span v-if="messageOf(row)" class="d-block text-muted small">
                                    {{ messageOf(row) }}
                                </span>
                            </span>
                        </template>
                    </el-table-column>
                </el-table>

                <div class="d-flex justify-content-end mt-2" v-if="itemsTotal > itemsPageSize">
                    <el-pagination
                        layout="prev, pager, next"
                        :total="itemsTotal"
                        :page-size="itemsPageSize"
                        :current-page="itemsPage"
                        @current-change="handlePageChange"
                    ></el-pagination>
                </div>
            </div>

            <!-- "งานล่าสุด": past jobs of this shop, so a result can be reopened
                 after the modal was closed. Element UI 2.4.11 has no el-drawer,
                 so this is an inline panel rather than a slide-over. -->
            <div v-if="recentShown">
                <hr />
                <h6>{{ $t("shopee_bulk_sync.recent_title") }}</h6>
                <el-table
                    :data="recentJobs"
                    header-row-class-name="thead-light"
                    max-height="260"
                    v-loading="recentLoading"
                    :empty-text="$t('shopee_bulk_sync.recent_empty')"
                >
                    <el-table-column :label="$t('shopee_bulk_sync.job_label')" width="90">
                        <template slot-scope="{ row }">#{{ row.id }}</template>
                    </el-table-column>
                    <el-table-column :label="$t('shopee_bulk_sync.col_created_at')" width="180">
                        <template slot-scope="{ row }">{{ row.created_at }}</template>
                    </el-table-column>
                    <el-table-column :label="$t('shopee_bulk_sync.col_mode')" width="150">
                        <template slot-scope="{ row }">
                            {{ row.dry_run ? $t("shopee_bulk_sync.mode_dry_run") : $t("shopee_bulk_sync.mode_real") }}
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('shopee_bulk_sync.col_status')" width="130">
                        <template slot-scope="{ row }">
                            <el-tag size="mini">{{ $t(jobStatusKeyOf(row)) }}</el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('shopee_bulk_sync.col_result')" min-width="200">
                        <template slot-scope="{ row }">
                            <span class="small">
                                {{ $t("shopee_bulk_sync.processed_of", { processed: row.processed, total: row.total }) }}
                            </span>
                        </template>
                    </el-table-column>
                    <el-table-column width="150" align="center">
                        <template slot-scope="{ row }">
                            <a href="#" @click.prevent="openJob(row)">{{ $t("shopee_bulk_sync.recent_open") }}</a>
                            <!-- #286: a finished past job can be exported
                                 without opening it first — after a run the
                                 operator's next move is usually "mail me the
                                 rejects", not "look at page 1 of 60". Only for
                                 finished rows: an unfinished one answers 409. -->
                            <a
                                href="#"
                                v-if="canExportJob(row)"
                                class="ml-2"
                                @click.prevent="handleExport(row.id)"
                            >{{ $t("shopee_bulk_sync.export_excel_short") }}</a>
                        </template>
                    </el-table-column>
                </el-table>
            </div>
        </div>

        <template slot="footer">
            <button
                type="button"
                class="btn btn-md btn-primary"
                :disabled="loading || !canStartDryRun"
                @click="handleDryRun()"
                v-if="showSettings"
                :title="$t('shopee_bulk_sync.btn_dry_run_hint')"
            >
                <i class="fa fa-spinner fa-spin mr-1" v-if="loading"></i>
                {{ $t("shopee_bulk_sync.btn_dry_run") }}
            </button>
            <button
                type="button"
                class="btn btn-md btn-success"
                :disabled="loading || !canStartRealRun"
                @click="handleRealRun()"
                v-if="showSettings"
            >
                <i class="fa fa-spinner fa-spin mr-1" v-if="loading"></i>
                {{ $t("shopee_bulk_sync.btn_real") }}
            </button>
            <button
                type="button"
                class="btn btn-md btn-danger"
                :disabled="cancelling || cancelRequested"
                @click="handleCancelJob()"
                v-if="showProgress && !isFinished"
            >
                <i class="fa fa-spinner fa-spin mr-1" v-if="cancelling"></i>
                {{ $t("shopee_bulk_sync.btn_cancel_job") }}
            </button>
            <button type="button" class="btn btn-md btn-outline-primary" @click="toggleRecent()">
                {{ $t("shopee_bulk_sync.btn_recent") }}
            </button>
            <button type="button" class="btn btn-md btn-link ml-auto" @click="close()">
                {{ $t("close") }}
            </button>
        </template>
    </modal>
</template>

<script>
// #280 — "sync ทั้งร้านเข้า seller" modal for /admin/product_platform.
// Spec: docs/specs/2026-09-22-shopee-master-product-sync-design.md §6C.3 (HTTP)
// + §6C.6 (FE).
//
// Three steps, one component: ① settings -> ② progress (polled) -> ③ per-item
// results, plus a "งานล่าสุด" panel that reopens a finished job. The
// asynchronous shape is the same one AmazePushModal uses (startPolling /
// stopPolling / fetchStatus / beforeDestroy), including the rule that matters
// most: a request that comes back "already running" is NOT an error — it hands
// back the running job's id and this modal attaches to it, so a second click
// can never start a second job and can never leave the operator guessing
// whether their click did anything (AmazePushModal does this for 422
// push_in_flight; here it is 409 bulk_in_flight, §6C.3).
//
// Everything decidable lives in @/utils/shopeeBulkSync — this repo's jest
// cannot mount a .vue file, so the request body, the percentage, the
// in-flight/finished predicates and the per-item reason key are all unit-tested
// there and only rendered here.
import {
    Select,
    Option,
    Table,
    TableColumn,
    Tag,
    Tabs,
    TabPane,
    Progress,
    Checkbox,
    CheckboxGroup,
    Switch,
    Pagination,
    Loading
} from "element-ui";
import swal from "sweetalert2";
import { ShopeeService } from "@/resource";
import {
    BULK_POLL_INTERVAL_MS,
    BULK_ITEMS_PAGE_SIZE,
    BULK_JOB_LIST_LIMIT,
    DEFAULT_ITEM_STATUS,
    SHOPEE_ITEM_STATUS_OPTIONS,
    bulkStartBody,
    bulkItemsParams,
    bulkJobPercent,
    bulkJobCounters,
    isBulkInFlight,
    isBulkFinished,
    bulkInFlightJobId,
    bulkItemReasonI18nKey,
    bulkItemRejects,
    bulkItemMessage,
    bulkItemProductId,
    bulkItemStatusTagType,
    bulkItemStatusI18nKey,
    bulkJobStatusI18nKey,
    bulkErrorI18nKey,
    bulkErrorDetail,
    bulkExportParams,
    bulkExportFilename,
    canExportBulk
} from "@/utils/shopeeBulkSync";
import {
    rejectDisplayText,
    rejectDetailText,
    shopeeSellerProductUrl
} from "@/utils/shopeeToSeller";
import { categoryResolutionTag } from "@/utils/shopeeCategoryMap";

// The results tabs. "all" maps to no `status` filter at all (bulkItemsParams
// drops it), which is why it cannot simply be the empty string here: el-tab-pane
// needs a name.
const ITEM_TABS = [
    { name: "all", labelKey: "shopee_bulk_sync.tab_all" },
    { name: "ok", labelKey: "shopee_bulk_sync.tab_ok" },
    { name: "rejected", labelKey: "shopee_bulk_sync.tab_rejected" },
    { name: "failed", labelKey: "shopee_bulk_sync.tab_failed" },
    { name: "skipped", labelKey: "shopee_bulk_sync.tab_skipped" }
];

export default {
    name: "shopee-bulk-sync-modal",
    components: {
        // Element UI is NOT registered globally in this app (only Input, Tooltip
        // and Popover are — src/plugins/globalComponents.js), so every el-* used
        // above has to be registered here.
        [Select.name]: Select,
        [Option.name]: Option,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Tabs.name]: Tabs,
        [TabPane.name]: TabPane,
        [Progress.name]: Progress,
        [Checkbox.name]: Checkbox,
        [CheckboxGroup.name]: CheckboxGroup,
        [Switch.name]: Switch,
        [Pagination.name]: Pagination
    },
    directives: {
        // v-loading is a DIRECTIVE, not a component, and it is not installed
        // globally either (src/plugins/globalComponents.js registers only
        // Input/Tooltip/Popover) — same local registration
        // Modern/CustomerDetailModal.vue uses.
        loading: Loading.directive
    },
    props: {
        show: { type: Boolean, default: false },
        // The Shopee shop id (platform_shop_id), as the page's shop filter
        // resolves it. Kept loose (String|Number) because the page reads it off
        // the seller row; bulkStartBody is what turns it into the string the Go
        // handler binds.
        shopId: { type: [String, Number], default: "" },
        // #284: the BACKOFFICE name of the seller row the page is filtered to
        // (bo_seller.seller_name). Passed in rather than looked up because the
        // page already has the row and this modal has no store module for
        // sellers.
        sellerName: { type: String, default: "" },
        // Legacy alias, kept so an older caller that still passes :shop-name
        // renders the seller name instead of a blank. The header's Shopee name
        // never comes from here — it comes from GET .../shops/:id/info.
        shopName: { type: String, default: "" }
    },
    data() {
        return {
            categoryId: null,
            // #284 — GET .../shops/:shop_id/info, fetched once per shop id. Null
            // means "not loaded, or the fetch failed"; either way the header
            // shows "-" and nothing else changes.
            shopInfo: null,
            shopInfoShopId: "",
            shopInfoLoading: false,
            itemStatus: DEFAULT_ITEM_STATUS.slice(),
            onlyUnlinked: false,
            loading: false,
            cancelling: false,
            // #286 — one at a time: a second click while the first download is
            // still building would ask the BE to render the same workbook twice.
            exporting: false,
            errorKey: "",
            errorDetail: "",
            attached: false,
            jobId: 0,
            job: null,
            polling: false,
            pollTimer: null,
            items: [],
            itemsTotal: 0,
            itemsTab: "all",
            itemsPage: 1,
            itemsLoading: false,
            recentShown: false,
            recentLoading: false,
            recentJobs: []
        };
    },
    computed: {
        categoryList() {
            return this.$store.getters["AppCatalogCategory/list"] || [];
        },
        itemStatusOptions() {
            return SHOPEE_ITEM_STATUS_OPTIONS;
        },
        itemTabs() {
            return ITEM_TABS;
        },
        itemsPageSize() {
            return BULK_ITEMS_PAGE_SIZE;
        },
        shopIdText() {
            return this.shopId === null || this.shopId === undefined ? "" : String(this.shopId);
        },
        // The shop's name ON SHOPEE. "" while loading and "" after a failed
        // fetch — the template turns both into "-".
        shopeeShopName() {
            return (this.shopInfo && this.shopInfo.shop_name) || "";
        },
        // The shop's name in the BACKOFFICE. `shopName` is the legacy prop name
        // for the same value.
        sellerNameText() {
            return String(this.sellerName || this.shopName || "").trim();
        },
        jobStatus() {
            return (this.job && this.job.status) || "";
        },
        isDryRunJob() {
            return !!(this.job && this.job.dry_run);
        },
        isFinished() {
            return isBulkFinished(this.jobStatus);
        },
        // No job row yet (the first poll is still in flight) counts as
        // "counting" too: rendering a 0% bar for that instant reads as a job
        // that is already stuck.
        isCounting() {
            if (!this.job) return true;
            return this.jobStatus === "queued" || this.jobStatus === "listing";
        },
        jobPercent() {
            return bulkJobPercent(this.job);
        },
        jobTotal() {
            return (this.job && Number(this.job.total)) || 0;
        },
        jobProcessed() {
            return (this.job && Number(this.job.processed)) || 0;
        },
        jobCreated() {
            return (this.job && Number(this.job.created_count)) || 0;
        },
        jobUpdated() {
            return (this.job && Number(this.job.updated_count)) || 0;
        },
        jobLastError() {
            return (this.job && this.job.last_error) || "";
        },
        cancelRequested() {
            return !!(this.job && this.job.cancel_requested);
        },
        counters() {
            return bulkJobCounters(this.job);
        },
        jobStatusKey() {
            return bulkJobStatusI18nKey(this.jobStatus);
        },
        jobStatusTagType() {
            if (this.jobStatus === "done") return "success";
            if (this.jobStatus === "failed") return "danger";
            if (this.jobStatus === "cancelled") return "warning";
            return "info";
        },
        // el-progress validates `status` against text/success/exception, so the
        // neutral case must be null (Vue skips the validator for null) rather
        // than "" — which would log a prop warning on every poll tick.
        progressBarStatus() {
            if (this.jobStatus === "done") return "success";
            if (this.jobStatus === "failed") return "exception";
            return null;
        },
        finishedTextKey() {
            if (this.jobStatus === "failed") return "shopee_bulk_sync.finished_failed";
            if (this.jobStatus === "cancelled") return "shopee_bulk_sync.finished_cancelled";
            return "shopee_bulk_sync.finished_done";
        },
        finishedAlertClass() {
            if (this.jobStatus === "failed") return "alert-danger";
            if (this.jobStatus === "cancelled") return "alert-warning";
            return "alert-success";
        },
        finishedIconClass() {
            return this.jobStatus === "done" ? "fa-check-circle" : "fa-exclamation-circle";
        },
        showSettings() {
            return this.jobId === 0;
        },
        showProgress() {
            return this.jobId > 0;
        },
        showResults() {
            return this.jobId > 0 && this.isFinished;
        },
        // #286 — the Export button's gate. The job object, not the status
        // string: canExportBulk takes either, and passing the row means a
        // `job` that is still null (the first poll is in flight) is false
        // rather than throwing.
        canExport() {
            return this.jobId > 0 && canExportBulk(this.job);
        },
        // A dry run needs a shop and at least one listing status; it never needs
        // a category, because it creates nothing.
        canStartDryRun() {
            return this.shopIdText !== "" && this.itemStatus.length > 0;
        },
        // The real run needs exactly what the dry run needs (#282). The old
        // extra `&& !!this.categoryId` guarded a 400 category_required that
        // §6D deleted: an empty category now means "resolve each product's
        // category from Shopee", which is the whole point of the feature, so
        // gating the button on it would disable the normal path.
        canStartRealRun() {
            return this.canStartDryRun;
        }
    },
    watch: {
        show(val) {
            if (val) this.reset();
            else this.stopPolling();
        }
    },
    beforeDestroy() {
        this.stopPolling();
    },
    methods: {
        // #284 — Seller Centre deep link for one item id, or "" when the id is
        // not a usable one (the template then renders plain text, because an
        // <a href=""> would reload the Backoffice and lose the job on screen).
        shopeeItemUrl: shopeeSellerProductUrl,
        // #642 — the listing's Shopee name off the /items row. Always a string:
        // the BE sends "" for an item it never fetched, and an older BE that has
        // no such field at all must read the same way here.
        itemNameOf(item) {
            var name = item && item.item_name;
            return name === null || name === undefined ? "" : String(name).trim();
        },
        // #284 — one lookup per shop id, fired when the modal opens.
        //
        // NEVER BLOCKS AND NEVER SHOUTS. The promise is not awaited by anything
        // and a failure leaves the header at "-" without touching errorKey: an
        // expired token would otherwise put a red alert over a modal whose actual
        // job — starting and watching a sync — does not need this endpoint at
        // all.
        loadShopInfo() {
            var self = this;
            var shopId = this.shopIdText;
            if (shopId === "") {
                this.shopInfo = null;
                this.shopInfoShopId = "";
                this.shopInfoLoading = false;
                return Promise.resolve();
            }
            // Already have it for THIS shop: the name does not change while a
            // modal is being opened and closed, and the BE caches it for an hour
            // anyway.
            if (this.shopInfoShopId === shopId && this.shopInfo) return Promise.resolve();
            this.shopInfo = null;
            this.shopInfoShopId = shopId;
            this.shopInfoLoading = true;
            return ShopeeService.ShopInfo(shopId)
                .then(function(resp) {
                    self.shopInfoLoading = false;
                    // The operator may have changed the shop filter while this was
                    // in flight; a late answer must not name the wrong shop.
                    if (self.shopIdText !== shopId) return;
                    self.shopInfo = (resp && resp.data) || null;
                })
                .catch(function() {
                    self.shopInfoLoading = false;
                    if (self.shopIdText !== shopId) return;
                    self.shopInfo = null;
                    // Cleared so the next open retries rather than remembering
                    // the failure — a refreshed token should show the name at
                    // once.
                    self.shopInfoShopId = "";
                });
        },
        itemStatusTagType: bulkItemStatusTagType,
        itemStatusKey: bulkItemStatusI18nKey,
        jobStatusKeyOf(job) {
            return bulkJobStatusI18nKey(job && job.status);
        },
        reasonKey: bulkItemReasonI18nKey,
        rejectsOf: bulkItemRejects,
        messageOf: bulkItemMessage,
        productIdOf: bulkItemProductId,
        rejectText(reject) {
            return rejectDisplayText(reject, this.$t.bind(this));
        },
        rejectDetail: rejectDetailText,
        itemActionKey(item) {
            var action = (item && item.action) || "";
            if (action === "create" || action === "update") return "shopee_bulk_sync.action." + action;
            return "shopee_bulk_sync.action.none";
        },
        // §6D.4: an `ok` item's detail carries the resolution object. A skipped
        // or failed item has none, and the column prints "-" rather than an
        // empty tag.
        categoryOf(item) {
            var detail = (item && item.detail) || {};
            var category = detail.category;
            if (!category || !category.name) return null;
            return category;
        },
        categoryTagOf(item) {
            return categoryResolutionTag(this.categoryOf(item));
        },
        skuSummary(item) {
            var detail = (item && item.detail) || {};
            var skus = detail.skus || {};
            return this.$t("shopee_bulk_sync.sku_summary", {
                create: Number(skus.create) || 0,
                update: Number(skus.update) || 0
            });
        },
        reset() {
            this.categoryId = null;
            this.itemStatus = DEFAULT_ITEM_STATUS.slice();
            this.onlyUnlinked = false;
            this.loading = false;
            this.cancelling = false;
            this.exporting = false;
            this.errorKey = "";
            this.errorDetail = "";
            this.attached = false;
            this.jobId = 0;
            this.job = null;
            this.items = [];
            this.itemsTotal = 0;
            this.itemsTab = "all";
            this.itemsPage = 1;
            this.recentShown = false;
            this.recentJobs = [];
            this.stopPolling();
            // Same category list the app-catalog product form and the per-item
            // to-seller modal use.
            this.$store.dispatch("AppCatalogCategory/fetchList", {});
            // #284: the Shopee shop name. Deliberately NOT reset above — it is
            // keyed on the shop id, so reopening the same shop reuses it and
            // switching shops refetches.
            this.loadShopInfo();
        },
        handleShowChange(val) {
            this.$emit("update:show", val);
        },
        close() {
            this.stopPolling();
            this.$emit("update:show", false);
        },
        clearMessages() {
            this.errorKey = "";
            this.errorDetail = "";
            this.attached = false;
        },
        // ตรวจสอบทั้งร้าน = dry run. Writes nothing on the seller side, so it
        // needs no confirmation and no category.
        handleDryRun() {
            if (!this.canStartDryRun) return Promise.resolve();
            return this.doStart(true);
        },
        // เริ่ม sync จริง = the real write across the whole shop. Confirmed
        // first. Since #282 it needs no category: an empty one means each
        // product takes the seller category its Shopee category maps to.
        handleRealRun() {
            var self = this;
            if (!this.canStartRealRun) return Promise.resolve();
            return swal
                .fire({
                    title: this.$t("shopee_bulk_sync.confirm_title"),
                    text: this.$t("shopee_bulk_sync.confirm_text"),
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonClass: "btn btn-success btn-fill",
                    cancelButtonClass: "btn btn-danger btn-fill",
                    confirmButtonText: this.$t("shopee_bulk_sync.confirm_ok"),
                    cancelButtonText: this.$t("shopee_bulk_sync.confirm_cancel"),
                    buttonsStyling: false
                })
                .then(function(result) {
                    if (!result || !result.value) return;
                    return self.doStart(false);
                });
        },
        doStart(dryRun) {
            var self = this;
            this.clearMessages();
            this.loading = true;
            var body = bulkStartBody(this.shopId, {
                categoryId: this.categoryId,
                itemStatus: this.itemStatus,
                onlyUnlinked: this.onlyUnlinked,
                dryRun: dryRun
            });
            return ShopeeService.BulkStart(body)
                .then(function(resp) {
                    self.loading = false;
                    var data = (resp && resp.data) || {};
                    self.attachToJob(Number(data.job_id) || 0);
                })
                .catch(function(err) {
                    self.loading = false;
                    // 409 bulk_in_flight is not a failure: one job per shop is
                    // the rule, so the right answer is to watch the job that is
                    // already running rather than to complain.
                    var running = bulkInFlightJobId(err);
                    if (running !== null) {
                        self.attached = true;
                        self.attachToJob(running);
                        return;
                    }
                    self.errorKey = bulkErrorI18nKey(err);
                    self.errorDetail = bulkErrorDetail(err);
                });
        },
        attachToJob(jobId) {
            if (!jobId) {
                this.errorKey = "shopee_bulk_sync.error.generic";
                return;
            }
            this.jobId = jobId;
            this.job = null;
            this.items = [];
            this.itemsTotal = 0;
            this.itemsPage = 1;
            this.itemsTab = "all";
            this.startPolling();
        },
        // Poll GET :id every BULK_POLL_INTERVAL_MS (§6C.6 step ②) until the job
        // leaves queued/listing/running.
        startPolling() {
            this.stopPolling();
            this.polling = true;
            this.fetchJob();
            this.pollTimer = setInterval(this.fetchJob, BULK_POLL_INTERVAL_MS);
        },
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
            this.polling = false;
        },
        fetchJob() {
            var self = this;
            if (!this.jobId) return Promise.resolve();
            return ShopeeService.BulkGet(this.jobId)
                .then(function(resp) {
                    var data = (resp && resp.data) || {};
                    self.job = data;
                    if (isBulkInFlight(data.status)) return;
                    self.stopPolling();
                    self.loadItems();
                    // Only a REAL run changed anything, so only a real run can
                    // make the table's seller-links badges stale.
                    if (data.dry_run !== true) {
                        self.$emit("synced", {
                            job_id: self.jobId,
                            status: data.status || "",
                            created_count: Number(data.created_count) || 0,
                            updated_count: Number(data.updated_count) || 0
                        });
                    }
                })
                .catch(function(err) {
                    // A failing POLL is not a failing job: the runner lives in
                    // the API container and keeps going. Say so, stop polling,
                    // and leave the job id on screen so it can be reopened from
                    // "งานล่าสุด".
                    self.stopPolling();
                    self.errorKey = "shopee_bulk_sync.error.poll_failed";
                    self.errorDetail = bulkErrorDetail(err);
                });
        },
        handleCancelJob() {
            var self = this;
            if (!this.jobId) return Promise.resolve();
            return swal
                .fire({
                    title: this.$t("shopee_bulk_sync.cancel_confirm_title"),
                    text: this.$t("shopee_bulk_sync.cancel_confirm_text"),
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonClass: "btn btn-danger btn-fill",
                    cancelButtonClass: "btn btn-secondary btn-fill",
                    confirmButtonText: this.$t("shopee_bulk_sync.btn_cancel_job"),
                    cancelButtonText: this.$t("shopee_bulk_sync.confirm_cancel"),
                    buttonsStyling: false
                })
                .then(function(result) {
                    if (!result || !result.value) return;
                    self.cancelling = true;
                    return ShopeeService.BulkCancel(self.jobId)
                        .then(function(resp) {
                            self.cancelling = false;
                            self.job = (resp && resp.data) || self.job;
                            if (isBulkFinished(self.job && self.job.status)) {
                                self.stopPolling();
                                self.loadItems();
                            }
                        })
                        .catch(function(err) {
                            self.cancelling = false;
                            // 409 bulk_finished = the job ended while the
                            // operator was reading the dialog. Re-read it
                            // instead of showing a scary box.
                            self.errorKey = bulkErrorI18nKey(err);
                            self.errorDetail = bulkErrorDetail(err);
                            return self.fetchJob();
                        });
                });
        },
        // v-model already carries the new tab by the time el-tabs emits
        // tab-click, but the pane is read off the event too so the fetch can
        // never lag a click behind.
        handleTabChange(tab) {
            if (tab && tab.name) this.itemsTab = tab.name;
            this.itemsPage = 1;
            return this.loadItems();
        },
        handlePageChange(page) {
            this.itemsPage = Number(page) || 1;
            return this.loadItems();
        },
        loadItems() {
            var self = this;
            if (!this.jobId) return Promise.resolve();
            this.itemsLoading = true;
            var params = bulkItemsParams(
                this.itemsTab === "all" ? "" : this.itemsTab,
                this.itemsPage,
                BULK_ITEMS_PAGE_SIZE
            );
            return ShopeeService.BulkItems(this.jobId, params)
                .then(function(resp) {
                    self.itemsLoading = false;
                    var data = (resp && resp.data) || {};
                    self.items = Array.isArray(data.items) ? data.items : [];
                    self.itemsTotal = Number(data.total) || 0;
                })
                .catch(function(err) {
                    self.itemsLoading = false;
                    self.items = [];
                    self.itemsTotal = 0;
                    self.errorKey = bulkErrorI18nKey(err);
                    self.errorDetail = bulkErrorDetail(err);
                });
        },
        toggleRecent() {
            this.recentShown = !this.recentShown;
            if (!this.recentShown) return Promise.resolve();
            return this.loadRecent();
        },
        loadRecent() {
            var self = this;
            this.recentLoading = true;
            return ShopeeService.BulkList({
                platform_shop_id: this.shopIdText,
                limit: BULK_JOB_LIST_LIMIT
            })
                .then(function(resp) {
                    self.recentLoading = false;
                    var data = (resp && resp.data) || {};
                    self.recentJobs = Array.isArray(data.jobs) ? data.jobs : [];
                })
                .catch(function(err) {
                    self.recentLoading = false;
                    self.recentJobs = [];
                    self.errorKey = bulkErrorI18nKey(err);
                    self.errorDetail = bulkErrorDetail(err);
                });
        },
        // Reopen a past job: the same progress + results blocks, polled again
        // when it turns out to still be running.
        openJob(job) {
            if (!job || !job.id) return Promise.resolve();
            this.clearMessages();
            this.recentShown = false;
            this.jobId = Number(job.id) || 0;
            this.job = job;
            this.items = [];
            this.itemsTotal = 0;
            this.itemsTab = "all";
            this.itemsPage = 1;
            if (isBulkInFlight(job.status)) {
                this.startPolling();
                return Promise.resolve();
            }
            return this.loadItems();
        },
        // #286 — "งานล่าสุด" offers the Excel link only on a finished row.
        canExportJob: canExportBulk,
        // handleExport downloads the .xlsx of one job.
        //
        //   handleExport()        -> the job on screen, filtered by the open tab
        //   handleExport(jobId)   -> a past job from "งานล่าสุด", every row
        //
        // THE FILENAME COMES OFF THE RESPONSE, not from a template built here:
        // the BE names the file after the SHOP, which this screen only knows as
        // an id. bulkExportFilename falls back to `shopee-sync-<id>.xlsx` when
        // the header did not survive a proxy, so the download always has a name
        // the operator can tell apart from the last one.
        handleExport(jobId) {
            var self = this;
            var id = Number(jobId) || this.jobId;
            if (!id || this.exporting) return Promise.resolve();
            // A row from "งานล่าสุด" exports the WHOLE job; the button on the
            // results step exports what the open tab shows.
            var params = jobId ? {} : bulkExportParams(this.itemsTab);
            this.exporting = true;
            return ShopeeService.BulkExport(id, params)
                .then(function(resp) {
                    self.exporting = false;
                    self.saveBlob(
                        (resp && resp.data) || null,
                        bulkExportFilename(resp && resp.headers, id)
                    );
                })
                .catch(function() {
                    self.exporting = false;
                    // The body of a failed request is a Blob (responseType is
                    // "blob" for the whole call), so there is no `code` to read
                    // without awaiting it. One sentence covers 409 / 404 / 5xx;
                    // the button is already disabled for the 409 case.
                    swal.fire({
                        title: self.$t("shopee_bulk_sync.export_failed"),
                        icon: "error"
                    });
                });
        },
        // saveBlob is the house download: object URL, a detached anchor, one
        // click, revoke. Kept here rather than in @/utils because it touches
        // document and window and this repo's jest cannot mount a .vue file —
        // everything DECIDABLE about the download (its name) is in the utils
        // module where a spec can call it.
        saveBlob(data, filename) {
            if (!data) return;
            var blob = new Blob([data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            var url = window.URL.createObjectURL(blob);
            var a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            // Not revoking leaks the whole workbook for the life of the tab, and
            // an operator exporting a dozen shops would hold a dozen of them.
            window.URL.revokeObjectURL(url);
        },
        openInCatalog(item) {
            var productId = bulkItemProductId(item);
            if (!productId) return;
            this.$router.push({
                name: "AppCatalogProductEdit",
                params: { id: String(productId) }
            });
        }
    }
};
</script>

<style>
.shopee-bulk-sync-modal .modal-body {
    max-height: 72vh;
    overflow-y: auto;
}

/* #284: clamp the Shopee item name to two lines — they run to ~120 characters
   and would otherwise stretch the row until the status and reason columns are
   pushed off the modal. The full text is in the tooltip. -webkit-line-clamp is
   the only cross-browser way to do this in the Chrome/Safari/Edge this
   Backoffice targets; max-height is the fallback for anything that ignores it,
   so the cell is never taller than two lines either way. */
.shopee-bulk-sync-modal .shopee-item-name {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-height: 2.8em;
    line-height: 1.4em;
    word-break: break-word;
}
</style>
